import type { FC } from 'react';
import { useMemo } from 'react';
import { Doughnut } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import { Box, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import type { BoxProps } from '@mui/system';

import TooltipContent from 'src/components/dashboard/charts/DoughnutChart/components/TooltipContent';
import NumberDifference from 'src/components/dashboard/DashboardPage/components/NumberDifference';
import type { BenchmarkDataSet } from 'src/components/dashboard/DashboardPage/widgets/DoughnutWidget/useDoughnutReData';

import { styles } from './index.styles';

interface DoughnutChartProps extends BoxProps {
  bigVersion?: boolean;
  data: number[];
  diffValue?: number | null;
  percentageValue: number | string;
  innerRadius?: number;
  benchmarkDataSet?: BenchmarkDataSet;
  showPercentageDefault?: boolean;
}

const DoughnutChart: FC<DoughnutChartProps> = ({
  bigVersion = false,
  data,
  diffValue,
  innerRadius = 70,
  percentageValue,
  sx,
  benchmarkDataSet,
  // used if percentageValue display is not adjusted via dataseries
  showPercentageDefault = true,
}) => {
  const { t } = useTranslation('dashboard');
  const { colors } = useTheme();

  const dataSet = useMemo(() => {
    const chartColors = [
      colors.doughnutVeryPositive,
      colors.doughnutPositive,
      colors.doughnutNeutral,
    ];

    return {
      backgroundColor: chartColors,
      borderColor: chartColors,
      borderWidth: 1,
      data: data,
      hoverBackgroundColor: chartColors,
    };
  }, [data, colors]);

  const doughnutOptions = useMemo(
    () => ({
      cutout: `${innerRadius}%`,
      maintainAspectRatio: false,
      responsive: true,
      plugins: {
        legend: { display: false },
        tooltip: { enabled: false },
      },
    }),
    [innerRadius],
  );

  return (
    <Tooltip arrow placement="right" title={<TooltipContent data={data} />}>
      <Box
        sx={[
          { position: 'relative' },
          bigVersion ? styles.chartBig : styles.chart,
          ...(Array.isArray(sx) ? sx : [sx]),
        ].filter(Boolean)}
      >
        <Doughnut
          data={{
            datasets: benchmarkDataSet
              ? [dataSet, benchmarkDataSet]
              : [dataSet],
          }}
          options={doughnutOptions}
        />

        <Stack direction="column" sx={styles.centerText}>
          <Typography
            component="div"
            sx={{
              fontSize: bigVersion ? 36 : 32,
              lineHeight: '2.5rem',
              fontWeight: 600,
            }}
          >
            {percentageValue}
            {showPercentageDefault ? '%' : ''}
          </Typography>
          <Typography
            className="DoughnutWidget-label"
            variant="body3"
            component="div"
            title={t('Labels.DiffToLastYear')}
            sx={{
              fontWeight: 500,
            }}
          >
            <NumberDifference
              comparisonDiff={diffValue}
              showArrow
              centerEmpty
            />
          </Typography>
        </Stack>
      </Box>
    </Tooltip>
  );
};

export default DoughnutChart;
